import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@sentry/types';
import { OptionsList } from 'gung-common';
import {
  AuthService,
  Customer,
  gungAddRemoveSpinner,
  GungBase,
  GungModalService,
  SelectedCustomerService
} from 'gung-standard';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs';

export interface TyloWarrantyForm extends GungBase {
  customer: {
    company: string;
    customerNumber: string;
    orderNumber: string;
    streetAddress: string;
    postalCodeCity: string;
    country: string;
    phoneNumber: string;
    emailAddress: string;
  };
  claim: {
    type: string;
    otherType: string;
    description: string;
  };
}

@Component({
    selector: 'helo-warranty-form',
    templateUrl: './helo-warranty-form.component.html',
    styleUrls: ['./helo-warranty-form.component.css'],
    standalone: false
})
export class HeloWarrantyFormComponent implements OnInit {
  private exportCustomerEmail = 'aftersales.fi@sauna360.com';
  private domesticCustomerEmail = 'Jalkimarkkinointi.fi@sauna360.com';

  warrantyForm: FormGroup;
  warrantyType: OptionsList[];

  currentCustomer: Customer;
  currentUser: User;

  constructor(
    protected formBuilder: FormBuilder,
    protected http: HttpClient,
    protected gungModalService: GungModalService,
    protected selectedCustomerService: SelectedCustomerService,
    protected authService: AuthService,
    protected translateService: TranslateService
  ) {}

  ngOnInit(): void {
    forkJoin([
      this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      this.authService.getCurrentUser().pipe(first())
    ]).subscribe(([customer, user]) => {
      this.currentCustomer = customer;
      this.currentUser = user;
      this.initForm();
    });
    this.initWarrantyType();
  }

  initWarrantyType() {
    const types = [
      {
        id:'Damaged in transport',
        name: this.translateService.instant('DAMAGED_IN_TRANSPORT')
      },{
        id: 'Amount of products',
        name: this.translateService.instant('AMOUNT_OF_PRODUCTS')
      },
      {
        id: 'Wrong product',
        name: this.translateService.instant('WRONG_PRODUCT')
      },
      {
        id: 'Price',
        name: this.translateService.instant('PRICE')
      },
      {
        id: "Invoicing",
        name: this.translateService.instant('INVOICING')
      },
      {
        id: "Technical fault",
        name: this.translateService.instant('TECHNICAL_FAULT')
      },
      {
        id: "Other",
        name: this.translateService.instant('OTHER')
      }
    ];
    this.warrantyType = [];
    for (const type of types) {
      this.warrantyType.push({ id: type.id, name: type.name });
    }
  }

  initForm() {
    this.warrantyForm = this.formBuilder.group({
      customer: this.formBuilder.group({
        company: this.formBuilder.control({ value: '', disabled: true }),
        customerNumber: this.formBuilder.control({ value: this.currentCustomer.id || '', disabled: true }),
        orderNumber: this.formBuilder.control({ value: null, disabled: false }, [Validators.required]),
        streetAddress: this.formBuilder.control({
          value: this.currentCustomer.extra.fr.ftgpostadr5 || '',
          disabled: true
        }),
        postalCodeCity: this.formBuilder.control({
          value: this.currentCustomer.extra.fr.ftglevpostnr || this.currentCustomer.extra.fr.ftgpostnr || '',
          disabled: true
        }),
        country: this.formBuilder.control({ value: this.currentCustomer.extra.fr.landskod || '', disabled: true }),
        phoneNumber: this.formBuilder.control({ value: '', disabled: false }, [Validators.required]),
        emailAddress: this.formBuilder.control({ value: this.currentUser.email || '', disabled: false }, [
          Validators.required
        ])
      }),
      claim: this.formBuilder.group({
        type: this.formBuilder.control({ value: '', disabled: false }, [Validators.required]),
        otherType: this.formBuilder.control({ value: '', disabled: true }),
        description: this.formBuilder.control({ value: '', disabled: false }, [Validators.required])
      })
    });
    this.subscribeWarrantyType();
  }

  subscribeWarrantyType() {
    this.warrantyForm
      .get('claim')
      .get('type')
      .valueChanges.subscribe(type => {
        this.warrantyForm.get('claim').get('otherType').disable();
        this.warrantyForm.get('claim').get('otherType').clearValidators();
        if (type === 'Other') {
          this.warrantyForm.get('claim').get('otherType').enable();
          this.warrantyForm.get('claim').get('otherType').setValidators(Validators.required);
        }
      });
  }

  sendWarranty({ target }) {
    const claimType = this.warrantyForm.get('claim').get('type').value;
    const customerNumber = this.warrantyForm.get('customer').get('customerNumber').value;

    if (!this.warrantyForm.valid) {
      this.warrantyForm.markAllAsTouched();
      return;
    }
    gungAddRemoveSpinner(target);
    if (claimType === 'Technical fault') {
      if ((customerNumber as string).startsWith('E')) {
        return this.postWarrantyFormForExportCustomers(target);
      }
      return this.postWarrantyFormForDomesticCustomers(target);
    }
    return this.postWarrantyFormForNormalCustomers(target);
  }

  postWarrantyFormForNormalCustomers(target) {
    return this.http.post<TyloWarrantyForm>(`json/send-warranty-form`, this.warrantyForm.getRawValue()).subscribe(
      result => {
        if (result) {
          this.gungModalService.openConfirmYesNoModal(undefined, 'SENT', { size: 'sm' }, 'OK', null);
        }
        gungAddRemoveSpinner(target);
        this.warrantyForm.reset();
        this.initForm();
      },
      error => {
        gungAddRemoveSpinner(target);
      }
    );
  }

  postWarrantyFormForExportCustomers(target) {
    return this.http
      .post<TyloWarrantyForm>(
        `json/send-warranty-form?emailToParam=${this.exportCustomerEmail}`,
        this.warrantyForm.getRawValue()
      )
      .subscribe(
        result => {
          if (result) {
            this.gungModalService.openConfirmYesNoModal(undefined, 'SENT', { size: 'sm' }, 'OK', null);
          }
          gungAddRemoveSpinner(target);
          this.warrantyForm.reset();
          this.initForm();
        },
        error => {
          gungAddRemoveSpinner(target);
        }
      );
  }

  postWarrantyFormForDomesticCustomers(target) {
    return this.http
      .post<TyloWarrantyForm>(
        `json/send-warranty-form?emailToParam=${this.domesticCustomerEmail}`,
        this.warrantyForm.getRawValue()
      )
      .subscribe(
        result => {
          if (result) {
            this.gungModalService.openConfirmYesNoModal(undefined, 'SENT', { size: 'sm' }, 'OK', null);
          }
          gungAddRemoveSpinner(target);
          this.warrantyForm.reset();
          this.initForm();
        },
        error => {
          gungAddRemoveSpinner(target);
        }
      );
  }
}
